import { useRef, useCallback, useState } from "react";

export interface CropRect {
    x: number;
    y: number;
    width: number;
    height: number;
}

interface CropOverlayProps {
    cropRect: CropRect;
    onCropChange: (rect: CropRect) => void;
    canvasWidth: number;
    canvasHeight: number;
    /** CSS display width of the canvas (from canvas.style.width), unaffected by parent transforms */
    displayWidth: number;
    /** CSS display height of the canvas (from canvas.style.height), unaffected by parent transforms */
    displayHeight: number;
}

type HandleType =
    | "nw"
    | "ne"
    | "sw"
    | "se"
    | "n"
    | "s"
    | "e"
    | "w"
    | "move"
    | null;

const MIN_CROP_SIZE = 50;
const HANDLE_HIT_RADIUS = 22;
const CORNER_HANDLE_LENGTH = 20;
const EDGE_HANDLE_LENGTH = 30;
const HANDLE_THICKNESS = 3;
const TOUCH_MARGIN = HANDLE_HIT_RADIUS;

export function CropOverlay({
    cropRect,
    onCropChange,
    canvasWidth,
    canvasHeight,
    displayWidth,
}: CropOverlayProps) {
    const [activeHandle, setActiveHandle] = useState<HandleType>(null);
    const [hoverHandle, setHoverHandle] = useState<HandleType>(null);
    const overlayRef = useRef<HTMLDivElement>(null);
    const dragStartRef = useRef<{
        canvasX: number;
        canvasY: number;
        startRect: CropRect;
        handle: Exclude<HandleType, null>;
    } | null>(null);

    // Layout-space scale: canvas pixels per CSS pixel (unaffected by parent CSS transforms)
    const layoutScale = displayWidth > 0 ? canvasWidth / displayWidth : 1;

    // Convert canvas coordinates to CSS layout coordinates
    const dX = cropRect.x / layoutScale;
    const dY = cropRect.y / layoutScale;
    const dW = cropRect.width / layoutScale;
    const dH = cropRect.height / layoutScale;

    /**
     * Convert client (visual) coordinates to CSS layout coordinates.
     * getBoundingClientRect() returns visual coords (affected by parent transform),
     * but CSS positions use layout coords. This bridges the gap.
     */
    const clientToLayout = useCallback(
        (clientX: number, clientY: number): { lx: number; ly: number } => {
            const overlay = overlayRef.current;
            if (!overlay) return { lx: 0, ly: 0 };

            const rect = overlay.getBoundingClientRect();
            const visualX = clientX - rect.left;
            const visualY = clientY - rect.top;

            // offsetWidth/Height = CSS layout dimensions (unaffected by transforms)
            // rect.width/height = visual dimensions (affected by transforms)
            const ratioX = rect.width > 0 ? overlay.offsetWidth / rect.width : 1;
            const ratioY = rect.height > 0 ? overlay.offsetHeight / rect.height : 1;

            return {
                lx: visualX * ratioX,
                ly: visualY * ratioY,
            };
        },
        []
    );

    const hitTest = useCallback(
        (clientX: number, clientY: number): HandleType => {
            const { lx: px, ly: py } = clientToLayout(clientX, clientY);

            // Crop rect bounds in CSS layout coordinates
            const left = dX;
            const top = dY;
            const right = dX + dW;
            const bottom = dY + dH;

            // Check corners first (within HANDLE_HIT_RADIUS of each corner)
            if (
                Math.abs(px - left) <= HANDLE_HIT_RADIUS &&
                Math.abs(py - top) <= HANDLE_HIT_RADIUS
            )
                return "nw";
            if (
                Math.abs(px - right) <= HANDLE_HIT_RADIUS &&
                Math.abs(py - top) <= HANDLE_HIT_RADIUS
            )
                return "ne";
            if (
                Math.abs(px - left) <= HANDLE_HIT_RADIUS &&
                Math.abs(py - bottom) <= HANDLE_HIT_RADIUS
            )
                return "sw";
            if (
                Math.abs(px - right) <= HANDLE_HIT_RADIUS &&
                Math.abs(py - bottom) <= HANDLE_HIT_RADIUS
            )
                return "se";

            // Check edge midpoints (within HANDLE_HIT_RADIUS)
            const midX = (left + right) / 2;
            const midY = (top + bottom) / 2;

            if (
                Math.abs(px - midX) <= HANDLE_HIT_RADIUS &&
                Math.abs(py - top) <= HANDLE_HIT_RADIUS
            )
                return "n";
            if (
                Math.abs(px - midX) <= HANDLE_HIT_RADIUS &&
                Math.abs(py - bottom) <= HANDLE_HIT_RADIUS
            )
                return "s";
            if (
                Math.abs(px - left) <= HANDLE_HIT_RADIUS &&
                Math.abs(py - midY) <= HANDLE_HIT_RADIUS
            )
                return "w";
            if (
                Math.abs(px - right) <= HANDLE_HIT_RADIUS &&
                Math.abs(py - midY) <= HANDLE_HIT_RADIUS
            )
                return "e";

            // Check if inside crop rect -> move
            if (px >= left && px <= right && py >= top && py <= bottom)
                return "move";

            return null;
        },
        [dX, dY, dW, dH, clientToLayout]
    );

    const clampRect = useCallback(
        (rect: CropRect): CropRect => {
            let { x, y, width, height } = rect;

            // Enforce minimum size
            width = Math.max(width, MIN_CROP_SIZE);
            height = Math.max(height, MIN_CROP_SIZE);

            // Clamp to image bounds
            x = Math.max(0, Math.min(x, canvasWidth - width));
            y = Math.max(0, Math.min(y, canvasHeight - height));
            width = Math.min(width, canvasWidth - x);
            height = Math.min(height, canvasHeight - y);

            return { x, y, width, height };
        },
        [canvasWidth, canvasHeight]
    );

    const handlePointerDown = useCallback(
        (e: React.PointerEvent) => {
            const handle = hitTest(e.clientX, e.clientY);
            if (!handle) return;

            e.preventDefault();
            e.stopPropagation();

            setActiveHandle(handle);

            // Convert to canvas coords via layout coords
            const { lx, ly } = clientToLayout(e.clientX, e.clientY);
            const canvasX = lx * layoutScale;
            const canvasY = ly * layoutScale;

            dragStartRef.current = {
                canvasX,
                canvasY,
                startRect: { ...cropRect },
                handle,
            };

            (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        },
        [hitTest, layoutScale, cropRect, clientToLayout]
    );

    const handlePointerMove = useCallback(
        (e: React.PointerEvent) => {
            // Use ref for sync check — activeHandle state may lag behind due to async setState
            if (!dragStartRef.current) {
                // Not dragging — update hover cursor
                const handle = hitTest(e.clientX, e.clientY);
                setHoverHandle(handle);
                return;
            }

            e.preventDefault();
            e.stopPropagation();

            const { lx, ly } = clientToLayout(e.clientX, e.clientY);
            const canvasX = lx * layoutScale;
            const canvasY = ly * layoutScale;

            const deltaX = canvasX - dragStartRef.current.canvasX;
            const deltaY = canvasY - dragStartRef.current.canvasY;
            const start = dragStartRef.current.startRect;
            // Use handle from ref (synchronous) instead of activeHandle state (async)
            const handle = dragStartRef.current.handle;

            let newRect: CropRect;

            switch (handle) {
                case "move": {
                    newRect = {
                        x: start.x + deltaX,
                        y: start.y + deltaY,
                        width: start.width,
                        height: start.height,
                    };
                    break;
                }
                case "nw": {
                    const newX = start.x + deltaX;
                    const newY = start.y + deltaY;
                    const newW = start.width - deltaX;
                    const newH = start.height - deltaY;
                    newRect = {
                        x: newW >= MIN_CROP_SIZE ? newX : start.x + start.width - MIN_CROP_SIZE,
                        y: newH >= MIN_CROP_SIZE ? newY : start.y + start.height - MIN_CROP_SIZE,
                        width: Math.max(newW, MIN_CROP_SIZE),
                        height: Math.max(newH, MIN_CROP_SIZE),
                    };
                    break;
                }
                case "ne": {
                    const newY = start.y + deltaY;
                    const newW = start.width + deltaX;
                    const newH = start.height - deltaY;
                    newRect = {
                        x: start.x,
                        y: newH >= MIN_CROP_SIZE ? newY : start.y + start.height - MIN_CROP_SIZE,
                        width: Math.max(newW, MIN_CROP_SIZE),
                        height: Math.max(newH, MIN_CROP_SIZE),
                    };
                    break;
                }
                case "sw": {
                    const newX = start.x + deltaX;
                    const newW = start.width - deltaX;
                    const newH = start.height + deltaY;
                    newRect = {
                        x: newW >= MIN_CROP_SIZE ? newX : start.x + start.width - MIN_CROP_SIZE,
                        y: start.y,
                        width: Math.max(newW, MIN_CROP_SIZE),
                        height: Math.max(newH, MIN_CROP_SIZE),
                    };
                    break;
                }
                case "se": {
                    newRect = {
                        x: start.x,
                        y: start.y,
                        width: Math.max(start.width + deltaX, MIN_CROP_SIZE),
                        height: Math.max(start.height + deltaY, MIN_CROP_SIZE),
                    };
                    break;
                }
                case "n": {
                    const newY = start.y + deltaY;
                    const newH = start.height - deltaY;
                    newRect = {
                        x: start.x,
                        y: newH >= MIN_CROP_SIZE ? newY : start.y + start.height - MIN_CROP_SIZE,
                        width: start.width,
                        height: Math.max(newH, MIN_CROP_SIZE),
                    };
                    break;
                }
                case "s": {
                    newRect = {
                        x: start.x,
                        y: start.y,
                        width: start.width,
                        height: Math.max(start.height + deltaY, MIN_CROP_SIZE),
                    };
                    break;
                }
                case "w": {
                    const newX = start.x + deltaX;
                    const newW = start.width - deltaX;
                    newRect = {
                        x: newW >= MIN_CROP_SIZE ? newX : start.x + start.width - MIN_CROP_SIZE,
                        y: start.y,
                        width: Math.max(newW, MIN_CROP_SIZE),
                        height: start.height,
                    };
                    break;
                }
                case "e": {
                    newRect = {
                        x: start.x,
                        y: start.y,
                        width: Math.max(start.width + deltaX, MIN_CROP_SIZE),
                        height: start.height,
                    };
                    break;
                }
                default:
                    return;
            }

            onCropChange(clampRect(newRect));
        },
        [layoutScale, clampRect, onCropChange, hitTest, clientToLayout]
    );

    const handlePointerUp = useCallback(
        (e: React.PointerEvent) => {
            if (!dragStartRef.current) return;
            e.preventDefault();
            e.stopPropagation();
            setActiveHandle(null);
            dragStartRef.current = null;
        },
        []
    );

    // Determine cursor based on handle under pointer
    const getCursor = (handle: HandleType): string => {
        switch (handle) {
            case "nw":
            case "se":
                return "nwse-resize";
            case "ne":
            case "sw":
                return "nesw-resize";
            case "n":
            case "s":
                return "ns-resize";
            case "e":
            case "w":
                return "ew-resize";
            case "move":
                return "move";
            default:
                return "default";
        }
    };

    // Third-points for rule-of-thirds grid
    const thirdW = dW / 3;
    const thirdH = dH / 3;

    return (
        <div
            className="touch-none"
            style={{
                position: "absolute",
                inset: -TOUCH_MARGIN,
                touchAction: "none",
                cursor: getCursor(activeHandle ?? hoverHandle),
            }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
        >
        <div
            ref={overlayRef}
            style={{
                position: "absolute",
                inset: TOUCH_MARGIN,
                pointerEvents: "none",
            }}
        >
            {/* Dim overlay — 4 strips around the crop area */}
            {/* Top strip */}
            <div
                style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    right: 0,
                    height: dY,
                    backgroundColor: "rgba(0,0,0,0.5)",
                    pointerEvents: "none",
                }}
            />
            {/* Bottom strip */}
            <div
                style={{
                    position: "absolute",
                    left: 0,
                    top: dY + dH,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(0,0,0,0.5)",
                    pointerEvents: "none",
                }}
            />
            {/* Left strip */}
            <div
                style={{
                    position: "absolute",
                    left: 0,
                    top: dY,
                    width: dX,
                    height: dH,
                    backgroundColor: "rgba(0,0,0,0.5)",
                    pointerEvents: "none",
                }}
            />
            {/* Right strip */}
            <div
                style={{
                    position: "absolute",
                    left: dX + dW,
                    top: dY,
                    right: 0,
                    height: dH,
                    backgroundColor: "rgba(0,0,0,0.5)",
                    pointerEvents: "none",
                }}
            />

            {/* Crop border */}
            <div
                style={{
                    position: "absolute",
                    left: dX,
                    top: dY,
                    width: dW,
                    height: dH,
                    border: "1px solid white",
                    boxSizing: "border-box",
                    pointerEvents: "none",
                }}
            />

            {/* Rule-of-thirds grid */}
            {/* Vertical lines */}
            <div
                style={{
                    position: "absolute",
                    left: dX + thirdW,
                    top: dY,
                    width: 1,
                    height: dH,
                    backgroundColor: "rgba(255,255,255,0.3)",
                    pointerEvents: "none",
                }}
            />
            <div
                style={{
                    position: "absolute",
                    left: dX + thirdW * 2,
                    top: dY,
                    width: 1,
                    height: dH,
                    backgroundColor: "rgba(255,255,255,0.3)",
                    pointerEvents: "none",
                }}
            />
            {/* Horizontal lines */}
            <div
                style={{
                    position: "absolute",
                    left: dX,
                    top: dY + thirdH,
                    width: dW,
                    height: 1,
                    backgroundColor: "rgba(255,255,255,0.3)",
                    pointerEvents: "none",
                }}
            />
            <div
                style={{
                    position: "absolute",
                    left: dX,
                    top: dY + thirdH * 2,
                    width: dW,
                    height: 1,
                    backgroundColor: "rgba(255,255,255,0.3)",
                    pointerEvents: "none",
                }}
            />

            {/* Corner handles — L-shaped */}
            {/* NW corner */}
            <div
                style={{
                    position: "absolute",
                    left: dX - HANDLE_THICKNESS / 2,
                    top: dY - HANDLE_THICKNESS / 2,
                    width: CORNER_HANDLE_LENGTH,
                    height: HANDLE_THICKNESS,
                    backgroundColor: "white",
                    pointerEvents: "none",
                }}
            />
            <div
                style={{
                    position: "absolute",
                    left: dX - HANDLE_THICKNESS / 2,
                    top: dY - HANDLE_THICKNESS / 2,
                    width: HANDLE_THICKNESS,
                    height: CORNER_HANDLE_LENGTH,
                    backgroundColor: "white",
                    pointerEvents: "none",
                }}
            />

            {/* NE corner */}
            <div
                style={{
                    position: "absolute",
                    left: dX + dW - CORNER_HANDLE_LENGTH + HANDLE_THICKNESS / 2,
                    top: dY - HANDLE_THICKNESS / 2,
                    width: CORNER_HANDLE_LENGTH,
                    height: HANDLE_THICKNESS,
                    backgroundColor: "white",
                    pointerEvents: "none",
                }}
            />
            <div
                style={{
                    position: "absolute",
                    left: dX + dW - HANDLE_THICKNESS / 2,
                    top: dY - HANDLE_THICKNESS / 2,
                    width: HANDLE_THICKNESS,
                    height: CORNER_HANDLE_LENGTH,
                    backgroundColor: "white",
                    pointerEvents: "none",
                }}
            />

            {/* SW corner */}
            <div
                style={{
                    position: "absolute",
                    left: dX - HANDLE_THICKNESS / 2,
                    top: dY + dH - HANDLE_THICKNESS / 2,
                    width: CORNER_HANDLE_LENGTH,
                    height: HANDLE_THICKNESS,
                    backgroundColor: "white",
                    pointerEvents: "none",
                }}
            />
            <div
                style={{
                    position: "absolute",
                    left: dX - HANDLE_THICKNESS / 2,
                    top: dY + dH - CORNER_HANDLE_LENGTH + HANDLE_THICKNESS / 2,
                    width: HANDLE_THICKNESS,
                    height: CORNER_HANDLE_LENGTH,
                    backgroundColor: "white",
                    pointerEvents: "none",
                }}
            />

            {/* SE corner */}
            <div
                style={{
                    position: "absolute",
                    left: dX + dW - CORNER_HANDLE_LENGTH + HANDLE_THICKNESS / 2,
                    top: dY + dH - HANDLE_THICKNESS / 2,
                    width: CORNER_HANDLE_LENGTH,
                    height: HANDLE_THICKNESS,
                    backgroundColor: "white",
                    pointerEvents: "none",
                }}
            />
            <div
                style={{
                    position: "absolute",
                    left: dX + dW - HANDLE_THICKNESS / 2,
                    top: dY + dH - CORNER_HANDLE_LENGTH + HANDLE_THICKNESS / 2,
                    width: HANDLE_THICKNESS,
                    height: CORNER_HANDLE_LENGTH,
                    backgroundColor: "white",
                    pointerEvents: "none",
                }}
            />

            {/* Edge handles — centered bars at midpoints */}
            {/* Top edge */}
            <div
                style={{
                    position: "absolute",
                    left: dX + dW / 2 - EDGE_HANDLE_LENGTH / 2,
                    top: dY - HANDLE_THICKNESS / 2,
                    width: EDGE_HANDLE_LENGTH,
                    height: HANDLE_THICKNESS,
                    backgroundColor: "white",
                    pointerEvents: "none",
                }}
            />
            {/* Bottom edge */}
            <div
                style={{
                    position: "absolute",
                    left: dX + dW / 2 - EDGE_HANDLE_LENGTH / 2,
                    top: dY + dH - HANDLE_THICKNESS / 2,
                    width: EDGE_HANDLE_LENGTH,
                    height: HANDLE_THICKNESS,
                    backgroundColor: "white",
                    pointerEvents: "none",
                }}
            />
            {/* Left edge */}
            <div
                style={{
                    position: "absolute",
                    left: dX - HANDLE_THICKNESS / 2,
                    top: dY + dH / 2 - EDGE_HANDLE_LENGTH / 2,
                    width: HANDLE_THICKNESS,
                    height: EDGE_HANDLE_LENGTH,
                    backgroundColor: "white",
                    pointerEvents: "none",
                }}
            />
            {/* Right edge */}
            <div
                style={{
                    position: "absolute",
                    left: dX + dW - HANDLE_THICKNESS / 2,
                    top: dY + dH / 2 - EDGE_HANDLE_LENGTH / 2,
                    width: HANDLE_THICKNESS,
                    height: EDGE_HANDLE_LENGTH,
                    backgroundColor: "white",
                    pointerEvents: "none",
                }}
            />
        </div>
        </div>
    );
}
